import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMicrophone, faMicrophoneSlash, faVideo, faVideoSlash, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { isAudioOutputSelectionSupported } from '../../utils/audioOutput';
import { unlockAudio } from '../../utils/unlockAudio';
import { preferredOutput } from '../../utils/meetingAudioOutput';
import '../../styles/meetings.css';

/**
 * The last screen before the room: see yourself, hear your microphone move,
 * pick the right devices, and decide whether to come in muted.
 *
 * The preview stream is handed to the room when joining (as `preview`), which
 * publishes it as it is: the browser is asked for the camera and microphone once,
 * not twice - Safari on iPhone asks the person every time. The room releases it.
 */

const stop = (stream) => stream?.getTracks().forEach((t) => t.stop());

const MeetingPreJoin = ({ meeting, joining, onJoin, onCancel, askName = false, name = '', onNameChange, cancelLabel = 'Back', note = '' }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const meterRef = useRef(null);
    // Set once the open devices have gone into the room: from then on it, not this screen, lets go of them.
    const handedOff = useRef(false);
    const mounted = useRef(true);
    const [reopen, setReopen] = useState(0);
    useEffect(() => () => { mounted.current = false; }, []);

    const [micOn, setMicOn] = useState(true);
    const [camOn, setCamOn] = useState(false);
    const [devices, setDevices] = useState({ mics: [], cams: [], outputs: [] });
    const [audioDeviceId, setAudioDeviceId] = useState('');
    const [videoDeviceId, setVideoDeviceId] = useState('');
    const [outputDeviceId, setOutputDeviceId] = useState('');
    // With Bluetooth earbuds or a headset connected, that is where the sound
    // starts - unless the person has already picked something themselves.
    const outputTouched = useRef(false);
    useEffect(() => {
        if (outputTouched.current || outputDeviceId || !isAudioOutputSelectionSupported()) return;
        const worn = preferredOutput(devices.outputs);
        if (worn) setOutputDeviceId(worn.deviceId);
    }, [devices.outputs, outputDeviceId]);
    const [notice, setNotice] = useState('');
    const [level, setLevel] = useState(0);

    const listDevices = useCallback(async () => {
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            setDevices({
                mics: all.filter((d) => d.kind === 'audioinput'),
                cams: all.filter((d) => d.kind === 'videoinput'),
                outputs: all.filter((d) => d.kind === 'audiooutput')
            });
        } catch { /* labels simply stay blank */ }
    }, []);

    // (Re)open the preview whenever a toggle or a device choice changes.
    useEffect(() => {
        let alive = true;
        let audioContext = null;
        let raf = 0;

        if (!handedOff.current) stop(streamRef.current);
        handedOff.current = false;
        streamRef.current = null;
        setLevel(0);
        if (videoRef.current) videoRef.current.srcObject = null;

        if (!micOn && !camOn) return undefined;
        if (!navigator.mediaDevices?.getUserMedia) {
            setNotice('This browser cannot use a camera or microphone here.');
            return undefined;
        }

        navigator.mediaDevices.getUserMedia({
            audio: micOn ? { deviceId: audioDeviceId ? { exact: audioDeviceId } : undefined, echoCancellation: true } : false,
            video: camOn ? { deviceId: videoDeviceId ? { exact: videoDeviceId } : undefined, width: { ideal: 1280 }, height: { ideal: 720 } } : false
        }).then((stream) => {
            if (!alive) { stop(stream); return; }
            streamRef.current = stream;
            setNotice('');
            if (videoRef.current && camOn) {
                videoRef.current.srcObject = stream;
                videoRef.current.play().catch(() => { });
            }
            listDevices(); // labels only appear once permission is granted

            if (micOn && stream.getAudioTracks().length) {
                audioContext = new (window.AudioContext || window.webkitAudioContext)();
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 512;
                audioContext.createMediaStreamSource(stream).connect(analyser);
                const data = new Uint8Array(analyser.fftSize);
                const tick = () => {
                    analyser.getByteTimeDomainData(data);
                    let peak = 0;
                    for (let i = 0; i < data.length; i += 1) peak = Math.max(peak, Math.abs(data[i] - 128));
                    meterRef.current = Math.min(1, peak / 64);
                    setLevel(meterRef.current);
                    raf = requestAnimationFrame(tick);
                };
                tick();
            }
        }).catch((err) => {
            if (!alive) return;
            const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
            setNotice(denied
                ? 'Your browser is blocking the camera or microphone. Allow access in the address bar, or join without them.'
                : 'We could not open your camera or microphone. You can still join and listen.');
            if (camOn) setCamOn(false);
            else setMicOn(false);
        });

        return () => {
            alive = false;
            cancelAnimationFrame(raf);
            audioContext?.close().catch(() => { });
            // Leaving the screen after handing the devices over: they are the room's now.
            if (!(handedOff.current && !mounted.current)) stop(streamRef.current);
            streamRef.current = null;
        };
    }, [micOn, camOn, audioDeviceId, videoDeviceId, reopen, listDevices]);

    useEffect(() => { listDevices(); }, [listDevices]);

    const join = async () => {
        // First of all, inside this tap: let the page play sound. A phone browser
        // forgets the tap before the meeting has connected (see utils/unlockAudio.js).
        unlockAudio();
        // The open devices go with the person into the room, which now owns them.
        const preview = streamRef.current;
        handedOff.current = Boolean(preview);
        try {
            await onJoin({ micOn, camOn, audioDeviceId, videoDeviceId, outputDeviceId, preview });
        } finally {
            // Still here and the devices were released (the join failed): open the preview again.
            if (mounted.current && preview?.getTracks().some((t) => t.readyState === 'ended')) {
                handedOff.current = false;
                setReopen((n) => n + 1);
            }
        }
    };

    const label = (d, fallback) => d.label || fallback;

    return (
        <div className="mt-prejoin">
            <div className="mt-prejoin-preview">
                <div className="mt-preview-box">
                    {camOn
                        ? <video ref={videoRef} muted playsInline className="mt-preview-video" />
                        : <div className="mt-preview-off">Camera is off</div>}
                    <div className="mt-preview-controls">
                        <button
                            type="button"
                            className={`mt-round ${micOn ? '' : 'off'}`}
                            onClick={() => setMicOn((v) => !v)}
                            aria-label={micOn ? 'Turn microphone off' : 'Turn microphone on'}
                        >
                            <FontAwesomeIcon icon={micOn ? faMicrophone : faMicrophoneSlash} />
                        </button>
                        <button
                            type="button"
                            className={`mt-round ${camOn ? '' : 'off'}`}
                            onClick={() => setCamOn((v) => !v)}
                            aria-label={camOn ? 'Turn camera off' : 'Turn camera on'}
                        >
                            <FontAwesomeIcon icon={camOn ? faVideo : faVideoSlash} />
                        </button>
                    </div>
                </div>

                <div className="mt-meter" title="Microphone level" aria-hidden="true">
                    <div className="mt-meter-fill" style={{ width: `${Math.round(level * 100)}%` }} />
                </div>
            </div>

            <div className="mt-prejoin-side">
                <h2>{meeting.title}</h2>
                <p className="mt-subtle">
                    {meeting.organizer ? `Organised by ${meeting.organizer}` : 'Ready to join?'}
                </p>

                {note && <p className="mt-notice">{note}</p>}
                {notice && <p className="mt-notice">{notice}</p>}

                {askName && (
                    <label className="mt-field">
                        <span>Your name</span>
                        <input
                            type="text"
                            value={name}
                            maxLength={120}
                            placeholder="How should others see you?"
                            onChange={(e) => onNameChange(e.target.value)}
                        />
                    </label>
                )}

                <label className="mt-field">
                    <span>Microphone</span>
                    <select value={audioDeviceId} onChange={(e) => setAudioDeviceId(e.target.value)}>
                        <option value="">Default</option>
                        {devices.mics.map((d, i) => (
                            <option key={d.deviceId || i} value={d.deviceId}>{label(d, `Microphone ${i + 1}`)}</option>
                        ))}
                    </select>
                </label>

                <label className="mt-field">
                    <span>Camera</span>
                    <select value={videoDeviceId} onChange={(e) => setVideoDeviceId(e.target.value)}>
                        <option value="">Default</option>
                        {devices.cams.map((d, i) => (
                            <option key={d.deviceId || i} value={d.deviceId}>{label(d, `Camera ${i + 1}`)}</option>
                        ))}
                    </select>
                </label>

                {isAudioOutputSelectionSupported() && devices.outputs.length > 0 && (
                    <label className="mt-field">
                        <span>Speaker</span>
                        <select value={outputDeviceId} onChange={(e) => { outputTouched.current = true; setOutputDeviceId(e.target.value); }}>
                            <option value="">Default</option>
                            {devices.outputs.map((d, i) => (
                                <option key={d.deviceId || i} value={d.deviceId}>{label(d, `Speaker ${i + 1}`)}</option>
                            ))}
                        </select>
                    </label>
                )}

                <div className="mt-prejoin-actions">
                    {onCancel && <button type="button" className="mt-btn ghost" onClick={onCancel} disabled={joining}>{cancelLabel}</button>}
                    <button type="button" className="mt-btn primary" onClick={join} disabled={joining || (askName && !name.trim())}>
                        {joining && <FontAwesomeIcon icon={faSpinner} spin />} Join now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MeetingPreJoin;
